"use client";

import React from "react";
import { ArrowRight, Briefcase, ShieldAlert, Landmark, Signature } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const checklistItems = [{
  icon: Landmark,
  title: "Precision Mowing & Edging",
  content: "We maintain your turf at the optimal height for health and aesthetics. Our sharp-blade technology ensures clean cuts that prevent disease, while our meticulous edging creates the sharp architectural lines that define a premium landscape."
}, {
  icon: Briefcase,
  title: "Organic Weed Control Systems",
  content: "Protect your garden's integrity with our proactive weeding protocols. We combine manual extraction with organic suppression techniques to ensure your flower beds and hardscapes remain pristine without the use of harsh chemicals."
}, {
  icon: ShieldAlert,
  title: "Hedge & Tree Pruning Artistry",
  content: "Our arborists specialize in structural pruning that promotes growth and maintains desired forms. From formal boxwood hedges to ornamental trees, we provide the seasonal shaping necessary for a sophisticated, well-managed appearance."
}, {
  icon: Signature,
  title: "Seasonal Debris & Leaf Management",
  content: "Maintain a clean canvas year-round. Our team provides comprehensive fall cleanups and spring preparation, removing organic debris and refreshing mulch to ensure your property always looks its absolute best regardless of the season."
}];
export default function BusinessSuccession() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="237371" id="lawn-garden-maintenance" className="bg-background text-foreground relative overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[800px]">
        
        {}
        <div className="relative flex flex-col justify-center p-8 md:p-16 lg:p-20 xl:p-24 z-10 border-b lg:border-b-0 lg:border-r border-border/20 bg-primary">
          {}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 opacity-20 pointer-events-none">
            <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] rounded-lg bg-secondary blur-[120px]" />
          </div>

          <div ref={leftRef} className={`max-w-xl transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-secondary/20 text-primary-foreground text-sm font-medium mb-8 border border-secondary/30">
              <span className="w-2 h-2 rounded-lg bg-secondary animate-pulse" />
              Organic Precision
            </div>
            
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-primary-foreground">
              The Art of <br />
              <span className="text-secondary">Pristine Care.</span>
            </h2>
            
            <p className="text-lg text-primary-foreground/80 mb-8 leading-relaxed font-default">
              Exceptional landscaping is defined by the details. Our maintenance programs are designed for those who demand perfection in every blade of grass and every sculpted hedge.
            </p>
            
            <p className="text-lg text-primary-foreground/80 mb-10 leading-relaxed font-default">
              At Campos V Landscaping, we blend horticultural expertise with architectural precision, ensuring your outdoor living spaces remain a vibrant, clean, and healthy extension of your home.
            </p>

            <Link to="/GetAQuotePage">
              <Button size="lg" className="group bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-lg shadow-lg">
                Request a Maintenance Plan
                <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </div>

        {}
        <div className="bg-background text-foreground flex flex-col justify-center p-8 md:p-16 lg:p-20 xl:p-24 z-10">
          <div ref={rightRef} className={`max-w-xl w-full mx-auto lg:mx-0 transition-all duration-1000 delay-200 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h3 className="font-default text-2xl md:text-3xl font-semibold mb-2 text-primary">
              Maintenance Excellence
            </h3>
            <p className="text-muted-foreground mb-8 font-default">
              Our comprehensive checklist for a flawless residential or commercial landscape.
            </p>

            <Accordion type="single" collapsible className="w-full" defaultValue="item-0">
              {checklistItems.map((item, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i} className="border-border/60 py-2">
                  <AccordionTrigger className="hover:no-underline hover:text-secondary transition-colors text-left">
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-accent flex items-center justify-center text-primary shadow-sm">
                        <item.icon className="size-5" />
                      </div>
                      <span className="font-default text-base md:text-lg font-medium text-foreground">
                        {item.title}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pl-14 pr-4 pb-6 text-base font-default">
                    {item.content}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>

      </div>
    </section>;
}